// libraries
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { TextField, InputAdornment, Grid, Stack, Button } from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

// queries
import { useGetCommoditiesQuery } from 'services/commodities'
import { useGetMaterialTypesQuery } from 'services/materialTypes'

// generic components
import ScaleViewMaterialCard from 'components/generic/card/ScaleViewMaterialCard'
import Loading from 'components/generic/Loading'

// helpers and utils
import rem from 'utils/rem'
import { SCALE_MATERIAL_VIEW_OPTIONS, DEFAULT_AXLE_DATA } from 'utils/constants'
import { setupScaleViewCards } from 'utils/setupScaleViewCards'

const MaterialEntryButtonGrid = ({
  setActiveMaterialForAxleWeights,
  isDisabled,
  updateMaterialEntryViewMode,
  setSelectedMaterial,
  onAddVehicle,
}) => {
  const [selectedCommodityId, setSelectedCommodityId] = useState(null)
  const [commoditiesList, setCommoditiesList] = useState([])
  const [selectedPriceEntries, setSelectedPriceEntries] = useState([])
  const [filteredCommodities, setFilteredCommodities] = useState([])
  const [searchCommodityText, setSearchCommodityText] = useState('')
  const [searchPriceEntryText, setSearchPriceEntryText] = useState('')
  const { settings } = useSelector(state => state.settings) || {}
  const { customizedMaterialFields } = settings?.purchaseSettings?.scaleView || {}
  const { enableAxleWeights, defaultNumberOfAxles } = settings?.purchaseSettings?.inboundTickets || {}
  const [gridIsLoading, setGridIsLoading] = useState(true)
  const { t } = useTranslation('inboundTicket')

  const { data: commoditiesData, isFetching: isFetchingCommoditiesData } = useGetCommoditiesQuery({ pageSize: 1000 })

  const { data: materialTypesData, isFetching: isFetchingMaterialTypesData } = useGetMaterialTypesQuery(
    { filter: JSON.stringify({ search: searchPriceEntryText, commodityId: selectedCommodityId }) },
    { skip: !selectedCommodityId },
  )

  useEffect(() => {
    if (commoditiesList && searchCommodityText) {
      const commodities = commoditiesList.filter(commodity =>
        commodity?.name?.match(new RegExp(searchCommodityText, 'i')),
      )
      setFilteredCommodities(commodities)
    } else {
      setFilteredCommodities([...commoditiesList])
    }
  }, [commoditiesList, searchCommodityText])

  useEffect(() => {
    if (isFetchingCommoditiesData || isFetchingMaterialTypesData) {
      setGridIsLoading(true)
      return
    }
    if (!selectedCommodityId && commoditiesData) {
      const customizedCommodities = setupScaleViewCards(commoditiesData, customizedMaterialFields || [])
      setCommoditiesList(customizedCommodities)
      setSelectedPriceEntries([])
      setGridIsLoading(false)
    } else if (selectedCommodityId && materialTypesData) {
      const customizedMaterials = {}
      customizedMaterialFields
        ?.find(field => field.id === selectedCommodityId)
        ?.materials?.forEach((material, index) => {
          if (!customizedMaterials[material.id]) {
            customizedMaterials[material.id] = {
              index,
              color: material?.color,
            }
          }
        })
      const formattedMaterials = materialTypesData.Items.filter(materialType => !!materialType.defaultPriceEntry)
        .map(materialType => ({
          ...materialType,
          color: customizedMaterials?.[materialType?.defaultPriceEntry?.id]?.color,
          name: materialType.commonName,
          materialName: materialType.commonName,
          materialCode: materialType.code,
          materialTypeId: materialType.id,
        }))
        .sort((a, b) => {
          const indexA = customizedMaterials?.[a?.defaultPriceEntry?.id]?.index
          if (indexA === undefined) return 1
          const indexB = customizedMaterials?.[b?.defaultPriceEntry?.id]?.index
          if (indexB === undefined) return -1
          return indexA - indexB
        })
      setCommoditiesList([])
      setSelectedPriceEntries([{ id: 'back' }, ...formattedMaterials])
      setGridIsLoading(false)
    }
  }, [selectedCommodityId, commoditiesData, materialTypesData, isFetchingCommoditiesData, isFetchingMaterialTypesData])

  const searchTextPlaceholder = selectedCommodityId ? t('search_materials') : t('search_commodities')
  const searchTextValue = selectedCommodityId ? searchPriceEntryText : searchCommodityText
  const buttonArray = selectedCommodityId ? selectedPriceEntries : filteredCommodities

  const onClickButton = buttonData => {
    if (isDisabled) return

    setSearchCommodityText('')
    setSearchPriceEntryText('')
    switch (true) {
      case !selectedCommodityId:
        setSelectedCommodityId(buttonData.id)
        setCommoditiesList([])
        break
      case buttonData.id === 'back':
        setSelectedCommodityId(null)
        setSelectedPriceEntries([])
        break
      default:
        setSelectedMaterial(buttonData)
        updateMaterialEntryViewMode(SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_NEW)
        break
    }
  }

  return (
    <>
      <Stack direction="row" sx={{ mb: 2, justifyContent: 'space-between' }} data-testid="scale-view-search-material">
        <TextField
          label={t('search')}
          id="outlined-start-adornment"
          placeholder={searchTextPlaceholder}
          sx={{ width: '65%', maxWidth: rem(300) }}
          value={searchTextValue}
          onChange={e => {
            const searchTextFunction = selectedCommodityId ? setSearchPriceEntryText : setSearchCommodityText
            return searchTextFunction(e.target.value)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          disabled={isDisabled}
        />
        {!isDisabled && (
          <Stack direction="row" gap={2}>
            {enableAxleWeights && (
              <Button
                id="split-axle-button"
                size="small"
                variant="outlined"
                onClick={() => {
                  setActiveMaterialForAxleWeights({
                    axleWeights: [...Array(defaultNumberOfAxles).keys()].map((_, index) => ({
                      ...DEFAULT_AXLE_DATA,
                      axleNumber: index + 1,
                    })),
                  })
                  setSelectedMaterial({})
                  updateMaterialEntryViewMode(SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_NEW)
                }}
              >
                {t('split_axle')}
              </Button>
            )}
            <Button
              id="quick-add-material"
              size="small"
              variant="outlined"
              onClick={() => {
                setSelectedMaterial({})
                updateMaterialEntryViewMode(SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_NEW)
              }}
            >
              {t('quick_add_material')}
            </Button>
            <Button
              id="add-vehicle-button"
              size="small"
              variant="outlined"
              onClick={() => onAddVehicle('scrap-vehicle')}
            >
              {t('add_vehicle')}
            </Button>
          </Stack>
        )}
      </Stack>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={2}
        sx={{ textAlign: 'center', flex: '1' }}
        data-testid="material-buttons"
      >
        {gridIsLoading ? (
          <Loading localized />
        ) : (
          buttonArray?.map(buttonData => (
            <Grid key={`grid-button-${buttonData.id}`} item xs={6} sm={4} md={3}>
              <ScaleViewMaterialCard buttonData={buttonData} onClick={() => onClickButton(buttonData)} />
            </Grid>
          ))
        )}
      </Grid>
    </>
  )
}

MaterialEntryButtonGrid.propTypes = {
  setActiveMaterialForAxleWeights: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  updateMaterialEntryViewMode: PropTypes.func,
  setSelectedMaterial: PropTypes.func,
  onAddVehicle: PropTypes.func,
}

MaterialEntryButtonGrid.defaultProps = {
  isDisabled: false,
  updateMaterialEntryViewMode: () => {},
  setSelectedMaterial: () => {},
  onAddVehicle: () => {},
}

export default MaterialEntryButtonGrid
